import { sortLabelSx } from "@app/theme/tokens";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import type { RegisterSort, RegisterSortKey } from "../api/spofRegister";
import type { SpofRecord } from "../types";
import { mw } from "../utils/format";
import { pidHref, spofCardHref } from "../utils/links";
import { RiskChip } from "./Chips";
import { PendingValue } from "./PendingValue";

const COLUMNS: {
  key: RegisterSortKey;
  label: string;
  align?: "right";
}[] = [
  { key: "spof_id", label: "SPOF ID" },
  { key: "unit", label: "Unit / Block" },
  { key: "description", label: "Description" },
  { key: "power_loss_mw", label: "Power Loss", align: "right" },
  { key: "risk_category", label: "Current Risk" },
  { key: "residual_risk_category", label: "Residual Risk" },
  { key: "pid_link", label: "P&ID" },
  { key: "mitigations", label: "Mitigations" },
];

export default function SpofRegisterTable({
  records,
  total,
  page,
  pageSize,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onShowMitigations,
}: {
  records: SpofRecord[];
  total: number;
  page: number;
  pageSize: number;
  sort: RegisterSort;
  onSortChange: (sort: RegisterSort) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onShowMitigations: (record: SpofRecord) => void;
}) {
  return (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map(({ key, label, align }) => (
                <TableCell key={key} align={align} sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort.key === key}
                    direction={
                      sort.key === key && sort.descending ? "desc" : "asc"
                    }
                    onClick={() =>
                      onSortChange({
                        key,
                        descending: sort.key === key && !sort.descending,
                      })
                    }
                    sx={sortLabelSx(sort.key === key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record) => (
              <TableRow
                key={`${record.power_plant_id}:${record.spof_id}`}
                hover
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Link
                    href={spofCardHref(record.power_plant_id, record.spof_id)}
                    sx={{ fontWeight: 600 }}
                  >
                    {record.spof_id}
                  </Link>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {record.unit ?? record.block ?? <PendingValue />}
                </TableCell>
                <TableCell>{record.description ?? <PendingValue />}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {record.power_loss_mw === null ? (
                    <PendingValue />
                  ) : (
                    `${mw(record.power_loss_mw)} MW`
                  )}
                </TableCell>
                <TableCell>
                  <RiskChip level={record.risk_category} />
                </TableCell>
                <TableCell>
                  <RiskChip level={record.residual_risk_category} />
                </TableCell>
                <TableCell>
                  <Tooltip
                    disableInteractive
                    title={
                      record.pid_link
                        ? "Open on P&ID"
                        : "No P&ID occurrence matched this SPOF"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!record.pid_link}
                        href={record.pid_link ? pidHref(record.pid_link) : ""}
                      >
                        <AccountTreeIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip
                    disableInteractive
                    title={
                      record.mitigations.length
                        ? `${record.mitigations.length} proposed mitigations`
                        : "No mitigation proposed"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={record.mitigations.length === 0}
                        onClick={() => onShowMitigations(record)}
                      >
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, value) => onPageChange(value + 1)}
        onRowsPerPageChange={(event) =>
          onPageSizeChange(Number(event.target.value))
        }
      />
    </Paper>
  );
}
